import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { clockIn, clockOut } from '../../models/timeclock/entry/entry';
import toast from '../../utils/toast';

import type { User } from '../../models/user/_types';
import type { Identifier } from '../../types/utilityTypes';

interface UseTimeClockParams {
  user: User;
  onSuccessCallback: () => void;
  notes: string;
}

interface ClockInOptions {
  taskId: Identifier;
  notes: string;
}

interface ClockOutOptions {
  notes: string;
}

export const useTimeClock = ({
  user,
  onSuccessCallback,
  notes,
}: UseTimeClockParams) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('translation', { keyPrefix: 'timetracking' });

  const clockInLogEntryMutation = useMutation({
    mutationFn: (options: ClockInOptions) => {
      if (!user?.id) {
        throw new Error('Attempted user update without ID');
      }
      if (!options.taskId) {
        throw new Error('Task ID is required');
      }
      return clockIn({
        taskId: options.taskId,
        userId: user.id,
        notes: options.notes,
      });
    },
    onSuccess: () => {
      toast.success('Clock updated');
      queryClient.invalidateQueries({ queryKey: ['entries'] });
      onSuccessCallback();
    },
    onError: () => {
      toast.error('Clock could not be updated');
    },
  });

  const clockOutLogEntryMutation = useMutation({
    mutationFn: (options: ClockOutOptions) => {
      if (!user?.id) {
        throw new Error('Attempted user update without ID');
      }
      return clockOut(user, options.notes);
    },
    onSuccess: () => {
      toast.success('Clock updated');
      queryClient.invalidateQueries({ queryKey: ['entries'] });
      onSuccessCallback();
    },
    onError: () => {
      toast.error('Clock could not be updated');
    },
  });

  const formikClockOut = {
    initialValues: {
      notes: notes,
    },
    validationSchema: Yup.object({
      notes: Yup.string().max(255),
    }),
    onSubmit: async (
      values: typeof formikClockOut.initialValues,
      { resetForm }: { resetForm: () => void }
    ) => {
      await clockOutLogEntryMutation.mutateAsync({ ...values });
      resetForm();
    },
  };

  const formikClockIn = {
    initialValues: {
      taskId: '' as Identifier,
      notes: notes,
    },
    validationSchema: Yup.object({
      taskId: Yup.number().required(t('Task selection is required')),
      notes: Yup.string().max(255),
    }),
    onSubmit: async (
      values: typeof formikClockIn.initialValues,
      { resetForm }: { resetForm: () => void }
    ) => {
      await clockInLogEntryMutation.mutateAsync({ ...values });
      resetForm();
    },
  };

  return {
    formikClockIn,
    formikClockOut,
  };
};
